import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EmissionValueWithSuffix } from '../../../../../../../../../../../shared/components/emission-value-with-suffix/EmissionValueWithSuffix';
import { DeclaredUnit } from '../../../../../../../../../../../shared/interfaces/IComponentDetails';
import { EmissionSymbol } from '../../../../../../../../../../../shared/interfaces/IFootprint';
import { IWasteManagement } from '../../../PcfWizard.type';

type ExpandableIndividualProcessWasteTreatmentDetailsProps = {
  wasteManagement?: IWasteManagement;
};

const ExpandableIndividualProcessWasteTreatmentDetails = ({
  wasteManagement,
}: ExpandableIndividualProcessWasteTreatmentDetailsProps): JSX.Element => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  const expandedContent = (
    <div className='flex flex-column gap-4'>
      <div className='flex flex-column w-full gap-3'>
        <div className='grid w-full grid-nogutter'>
          {/* Waste per Piece */}
          <div className='col-6 flex flex-column gap-1 pr-2'>
            <span className='text-gray-700 font-medium'>
              {t('calculationDetailsPage.sections.individualProcess.wasteTreatment.wastePerPiece')}
            </span>
            <div className='flex flex-row gap-1 text-lg'>
              <span>{wasteManagement?.waste_per_piece}</span>
              <span>kg</span>
            </div>
          </div>

          {/* Sprue Treatment */}
          <div className='col-6 flex flex-column gap-1 pl-2'>
            <span className='text-gray-700 font-medium'>
              {t('calculationDetailsPage.sections.individualProcess.wasteTreatment.wasteTreatment')}
            </span>
            <span className='text-lg'>{wasteManagement?.waste_treatment?.name}</span>
          </div>
        </div>

        <div className='grid w-full grid-nogutter'>
          {/* Source */}
          <div className='col-6 flex flex-column gap-1 pr-2'>
            <span className='text-gray-700 font-medium'>
              {t('calculationDetailsPage.sections.individualProcess.wasteTreatment.source')}
            </span>
            <span>{wasteManagement?.waste_treatment?.source}</span>
          </div>

          {/* Emission Factor */}
          <div className='col-6 flex flex-column gap-1 pl-2'>
            <span className='text-gray-700 font-medium'>
              {t(
                'calculationDetailsPage.sections.injectionMoldingProcess.wasteTreatment.scrap.emissionFactor',
              )}
            </span>

            <EmissionValueWithSuffix
              emissionValue={wasteManagement?.waste_treatment?.pcfExcludingBiogenic}
              declaredUnit={DeclaredUnit.kilogram}
              unitaryProductAmount={1}
              emissionSymbol={EmissionSymbol.KG_CO2E}
              abbreviateUnit={true}
              emissionValueDecimals={6}
              numericalValueTextClassName='text-lg'
              suffixTextClassName='text-lg'
            />
          </div>
        </div>

        {/* Comment */}
        {wasteManagement?.waste_treatment?.comment && (
          <div className='flex flex-column gap-1 w-full'>
            <span className='text-gray-700 font-medium'>
              {t(
                'calculationDetailsPage.sections.injectionMoldingProcess.wasteTreatment.scrap.comment',
              )}
            </span>
            <span>{wasteManagement?.waste_treatment?.comment}</span>
          </div>
        )}
      </div>

      <Divider className='p-2 m-0 opacity-60' />
    </div>
  );

  const onExpandClick = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className='flex flex-column gap-3 w-full'>
      {/* Title & Expand Button */}
      <div className='flex flex-row gap-2 align-items-center'>
        <span className='text-lg font-medium'>
          {t('calculationDetailsPage.sections.injectionMoldingProcess.wasteTreatment.title')}
        </span>

        <Button
          icon={isExpanded ? 'pi pi-angle-up' : 'pi pi-angle-down'}
          onClick={onExpandClick}
          text
          className='p-2 m-0 -my-1 text-black-alpha-90 w-min hover:bg-black-alpha-20 border-round-lg bg-black-alpha-10 opacity-60'
          pt={{ icon: { className: 'font-bold' } }}
        />
      </div>

      {/* Expanded Content */}
      {isExpanded && expandedContent}

      {/* Emissions */}
      <div className='flex flex-column gap-1'>
        <span className='text-gray-700 font-medium'>
          {t('calculationDetailsPage.inputMaterial.emissions')}
        </span>

        <EmissionValueWithSuffix
          emissionValue={wasteManagement?.emissions}
          declaredUnit={DeclaredUnit.piece}
          unitaryProductAmount={1}
          emissionSymbol={EmissionSymbol.KG_CO2E}
          abbreviateUnit={true}
          emissionValueDecimals={6}
          numericalValueTextClassName='text-lg'
          suffixTextClassName='text-lg'
        />
      </div>
    </div>
  );
};

export default ExpandableIndividualProcessWasteTreatmentDetails;
